import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Award, FileText, RefreshCw, Loader2, CheckCircle2, Clock, XCircle, AlertTriangle } from 'lucide-react';

const VERDE = '#10B981';
const AMBAR = '#F59E0B';
const AZUL = '#0369A1';
const ROJO = '#DC2626';
const GRIS = '#6B7280';

export default function AdminCiclos() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.get('/admin/ciclos');
      setDatos(r.data);
    } catch (e) {
      toast.error('No se pudieron cargar los ciclos');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const entregar = async (c) => {
    if (!window.confirm(
      `Vas a entregar ${c.distintivo_code} a:\n\n${c.cliente}\n\n` +
      `Aprobó ${c.aprobadas} auditorías seguidas.\n` +
      `El distintivo tendra vigencia de un ano desde hoy.\n\n¿Continuar?`)) return;
    setOcupado(c.id);
    try {
      await api.post(`/admin/ciclos/${c.id}/entregar`, { distintivo_code: c.distintivo_code, notas: '' });
      toast.success('Distintivo entregado');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo entregar');
    } finally { setOcupado(null); }
  };

  const cerrar = async (c) => {
    const renueva = window.confirm(
      `Cierre de ciclo anual de ${c.cliente} (todos sus distintivos).\n\n` +
      `Aceptar = el cliente RENUEVA (se abre un ciclo nuevo)\n` +
      `Cancelar = el cliente NO renueva`);
    setOcupado(c.id);
    try {
      const r = await api.post(`/admin/ciclos/${c.id}/cerrar`, { renueva, notas: '' });
      toast.success(renueva ? 'Ciclo cerrado y renovado' : 'Ciclo cerrado sin renovacion');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cerrar');
    } finally { setOcupado(null); }
  };

  const verPdf = (c) => {
    window.open(`/api/admin/ciclos/${c.id}/cierre-anual.pdf`, '_blank');
  };

  const ciclos = datos?.ciclos || [];

  return (
    <AdminLayout title="Ciclos de certificacion">
      <div style={{ maxWidth: 1150 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Ciclos de certificacion</h1>
          <button onClick={cargar} disabled={cargando}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1px solid #DDD', borderRadius: 8, background: '#FFF', cursor: 'pointer' }}>
            <RefreshCw size={15} /> Actualizar
          </button>
        </div>
        <p style={{ color: GRIS, fontSize: 14, marginTop: 6, marginBottom: 24 }}>
          Cada ciclo anual tiene 12 auditorías mensuales. Cada distintivo lleva su propio conteo y se adquiere con 3
          aprobadas seguidas; si una reprueba antes, el conteo vuelve a cero. Después, una
          reprobada lo deja en observación y dos seguidas, o un Punto de Seguridad de Vida,
          lo suspenden. El mes 12 cierra el ciclo.
        </p>

        {cargando ? (
          <div style={{ display: 'flex', gap: 10, color: GRIS, padding: 40 }}>
            <Loader2 size={18} className="animate-spin" /> Cargando...
          </div>
        ) : !ciclos.length ? (
          <div style={{ textAlign: 'center', padding: 60, color: GRIS,
            border: '1px dashed #DDD', borderRadius: 12 }}>
            <Award size={38} style={{ opacity: 0.35, marginBottom: 12 }} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Sin ciclos activos</div>
            <div style={{ fontSize: 13 }}>
              Los ciclos se crean automaticamente al programar la primera auditoria de un cliente.
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            {ciclos.map((c) => {
              const listo = c.listo;
              const suspendido = c.estado_distintivo === 'suspendido';
              const observacion = c.estado_distintivo === 'observacion';
              const color = suspendido ? ROJO : observacion ? AMBAR : c.distintivo_emitido ? VERDE : listo ? AMBAR : AZUL;
              return (
                <div key={`${c.id}-${c.distintivo_code}`} style={{ border: '1px solid #E5E7EB', borderRadius: 12,
                  padding: 18, background: '#FFF' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: 14 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{c.cliente}</div>
                      <div style={{ color: GRIS, fontSize: 12.5, marginTop: 2 }}>
                        {c.distintivo_code} · inicio {(c.inicio || '').slice(0, 10)}
                        {c.promedio != null && ` · promedio ${c.promedio}%`}
                      </div>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 11px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: color + '18', color }}>
                      {suspendido || observacion ? <AlertTriangle size={13} /> : c.distintivo_emitido ? <CheckCircle2 size={13} />
                        : listo ? <Award size={13} /> : <Clock size={13} />}
                      {c.etapa}
                    </span>
                  </div>

                  <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between',
                    fontSize: 12.5, color: GRIS }}>
                    <span>{c.aprobadas} de {c.requeridas} auditorías aprobadas seguidas</span>
                    <span>visita {c.completadas} de 12</span>
                  </div>
                  <div style={{ height: 9, background: '#F0F1F3', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, c.porcentaje)}%`,
                      background: color, borderRadius: 6, transition: 'width .3s' }} />
                  </div>

                  <div style={{ marginTop: 12, fontSize: 13, color: '#3A4046' }}>{c.mensaje}</div>

                  <div style={{ marginTop: 14, display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                    <button onClick={() => verPdf(c)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 13px', border: '1px solid #DDD', borderRadius: 8,
                        background: '#FFF', cursor: 'pointer', fontSize: 13 }}>
                      <FileText size={14} /> Reporte de cierre (PDF)
                    </button>
                    {listo && (
                      <button onClick={() => entregar(c)} disabled={ocupado === c.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '7px 13px', border: 'none', borderRadius: 8,
                          background: VERDE, color: '#FFF', cursor: 'pointer', fontSize: 13,
                          fontWeight: 600 }}>
                        {ocupado === c.id ? <Loader2 size={14} className="animate-spin" />
                          : <Award size={14} />} Entregar distintivo
                      </button>
                    )}
                    {c.completadas >= 12 && c.estado !== 'completed' && (
                      <button onClick={() => cerrar(c)} disabled={ocupado === c.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '7px 13px', border: '1px solid ' + AMBAR, borderRadius: 8,
                          background: '#FFF', color: '#92400E', cursor: 'pointer', fontSize: 13 }}>
                        {ocupado === c.id ? <Loader2 size={14} className="animate-spin" />
                          : <XCircle size={14} />} Cerrar ciclo (mes 12)
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
